import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { initRevenueCat, rcLogin, rcLogout } from '../revenuecat';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const handleAuthUser = async (firebaseUser) => {
    if (firebaseUser) {
      // Ако е регистриран с email, трябва да е верифициран
      if (!firebaseUser.emailVerified && firebaseUser.providerData[0]?.providerId === 'password') {
        setUser(null);
        userRef.current = null;
        setLoading(false);
        return;
      }
      setUser(firebaseUser);
      userRef.current = firebaseUser;
      // ↓ ДОБАВИ: свързваме RevenueCat с Firebase UID (критично за webhook-а)
      rcLogin(firebaseUser.uid);
      try {
        const token = await firebaseUser.getIdToken();
        const res = await fetch(`${API_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUserPlan(data);
        }
      } catch (e) {
        console.error('Error loading user plan:', e);
      }
    } else {
      setUser(null);
      userRef.current = null;
      setUserPlan(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, handleAuthUser);

    // iOS Capacitor workaround: onAuthStateChanged може да не fire-ва
    let pollInterval;
    let platform = 'web';
    try {
      if (window.Capacitor && window.Capacitor.getPlatform) {
        platform = window.Capacitor.getPlatform();
      }
    } catch (e) { }

    if (platform === 'ios') {
      pollInterval = setInterval(() => {
        const currentUser = auth.currentUser;
        if (currentUser && !userRef.current) {
          handleAuthUser(currentUser);
        }
        if (!currentUser && userRef.current) {
          handleAuthUser(null);
        }
      }, 500);
    }

    return () => {
      unsubscribe();
      if (pollInterval) clearInterval(pollInterval);
    };
    // handleAuthUser нарочно НЕ е dependency — не искаме да пресъздаваме
    // auth listener-а при всеки render; той трябва да се закачи веднъж.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  // Safety timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, []);
  // Инициализираме RevenueCat веднъж при старт (само iOS)
  useEffect(() => {
    initRevenueCat();
  }, []);

  const signOut = async () => {
    await rcLogout(); 
    await firebaseSignOut(auth);
    setUser(null);
    userRef.current = null;
    setUserPlan(null);
  };
  const deleteAccount = async () => {
    if (!user) return { ok: false };
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/user/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return { ok: false };
      }
      // Успешно изтрит — излизаме от профила
      await rcLogout(); 
      await firebaseSignOut(auth);
      setUser(null);
      userRef.current = null;
      setUserPlan(null);
      return { ok: true };
    } catch (e) {
      console.error('Delete account error:', e);
      return { ok: false };
    }
  };
  const getAuthHeader = async () => {
    if (!user) return {};
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  };

  const refreshPlan = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserPlan(data);
      }
    } catch (e) {
      console.error('Error refreshing plan:', e);
    }
  };

  const value = {
    user,
    userPlan,
    loading,
    signOut,
    deleteAccount,
    getAuthHeader,
    refreshPlan,
    isPro: userPlan?.plan === 'pro',
    predictionsLeft: userPlan ? userPlan.dailyLimit - userPlan.predictionsToday : 0,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
